// React
import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useOutletContext, useNavigate, Outlet } from 'react-router-dom'

// Antd and MDI
import { Footer } from 'antd/lib/layout/layout'
import { Layout } from 'antd'
// Components
import SearchHeader from '../layout/SearchHeader'
import DynamicTable from '../../containers/DynamicTable'
import Pagination from '../utility/Pagination'

// Utils
import { SearchSegmentsEnum, PageSizes } from '../../utils/Enums'
import { Loader, filterDataFunction, capitalizeFirstLetter, documentOrdersMapFunction } from '../../utils/Functions'
import { biltyManagementColumns } from '../../utils/TableColumns'

// Recoil
import { biltyOpsObjectSelector, biltyTabSelector, dataByIDSelector, trackersOptionsSelector } from '../../recoil/selectors'
import { useRecoilValue } from 'recoil'

// Hooks
import useFetchQuery from '../../hooks/useFetchQuery'
import useFetchLazyQuery from '../../hooks/useFetchLazyQuery'

// ERROR BOUNDARY
import { ErrorBoundary } from 'react-error-boundary'
import FallBackUI from '../errors/FallBackUI'
import TableHeader from '../utility/TableHeader'
import { LevelContext } from '../../services/LevelContext'
import { userAtom } from '../../recoil/atoms'

const { Content } = Layout

const BiltyManagementTable = () => {
	const { orderType, id, modalType, tabStatus = 'issuance', selectedBiltyNumber } = useParams()
	const { collection } = useOutletContext()

	const segment = useMemo(() => capitalizeFirstLetter(orderType), [orderType])

	const navigator = useNavigate()
	const user = useRecoilValue(userAtom)
	const [loading, setLoading] = useState(false)
	const [keyword, setKeyword] = useState('')
	const [selectedRowKeys, setSelectedRowKeys] = useState([])
	const [showMissingKanta, setShowMissingKanta] = useState(false)
	const advancedSearch = useFetchLazyQuery({ segment, action: 'normal', type: 'search' })

	const [pageInfo, setPageInfo] = useState({ pageNo: 1, pageSize: 10 })
	const [searchData, setSearchData] = useState({ data: [], collection: '' })
	const [filterProperty, setFilterProperty] = useState(SearchSegmentsEnum[segment]?.filterProperties?.[tabStatus]?.[0])
	const [backendPageInfo, setBackendPageInfo] = useState({ pageNo: 1, pageSize: PageSizes[pageInfo.pageSize].backend })
	const [filteredOrdersCount, setFilteredOrdersCount] = useState(0)

	const trackers = useRecoilValue(trackersOptionsSelector)
	const collectionData = useRecoilValue(biltyTabSelector({ segment, tabStatus, showMissingKanta }))
	const biltyOpUsers = useRecoilValue(biltyOpsObjectSelector)
	const modalData = useRecoilValue(dataByIDSelector({ segment, tabStatus, collection, id: id?.split('+') }))
	const selectedOrders = useRecoilValue(dataByIDSelector({ segment, tabStatus, collection, id: selectedRowKeys }))

	useFetchQuery({
		segment: 'Trackers',
		action: 'normal',
	})

	useFetchQuery({
		segment: 'BiltyOps',
		action: 'normal',
	})

	const queryResult = useFetchQuery({
		segment,
		action: tabStatus === 'cancelled' ? 'alternate' : 'normal',
		backendPageInfo,
		subscription: true,
		subscriptionVariables:
			tabStatus !== 'cancelled'
				? { statuses: ['Order Confirmed', 'Order Ongoing', 'Partial Ongoing', 'Partial Completed', 'Order Completed', 'Order Cancelled'], type: segment }
				: {
						status: ['Job Cancelled'],
						...(segment === 'Import'
							? { movementType: ['Import', 'ImportShifting', 'DoubleTwenty'], subMovementType: ['Import', 'LongHaul'] }
							: segment === 'Export'
							? { movementType: ['Export', 'DoubleTwenty'], subMovementType: ['Export'] }
							: segment === 'LongHaul' && { movementType: ['LongHaul'], subMovementType: ['LongHaul'] }),
				  },
		variables: tabStatus !== 'cancelled' ? { segment: segment } : { status: 'Job Cancelled' },
	})

	useEffect(() => {
		setLoading(queryResult?.loading || advancedSearch?.loading)
	}, [collection, queryResult, advancedSearch]) // eslint-disable-line

	useEffect(() => {
		setBackendPageInfo({ pageNo: 1, pageSize: PageSizes[pageInfo.pageSize].backend })
	}, [pageInfo.pageSize]) // eslint-disable-line

	useEffect(() => {
		setBackendPageInfo({ pageNo: 1, pageSize: 20 })
	}, [segment, collection]) // eslint-disable-line

	useEffect(() => {
		setSelectedRowKeys([])
		setPageInfo({ pageNo: 1, pageSize: 10 })
	}, [segment, tabStatus]) // eslint-disable-line

	useEffect(() => {
		setPageInfo((prev) => ({ ...prev, pageNo: 1 }))
	}, [keyword]) // eslint-disable-line

	useEffect(() => {
		const FilteredData = filterDataFunction({
			keyword,
			segment,
			tabStatus,
			filterProperty,
			selectedOrders,
			FilteredOrders: collectionData,
		})
		setFilteredOrdersCount(FilteredData?.length)
		setSearchData({
			data:
				selectedOrders?.length !== 0
					? FilteredData
					: FilteredData?.slice(pageInfo.pageNo * pageInfo.pageSize - pageInfo.pageSize, pageInfo.pageNo * pageInfo.pageSize),
			collection: collection,
		})
	}, [collectionData, keyword, filterProperty, selectedOrders, selectedRowKeys, segment, tabStatus, collection, pageInfo])

	return (
		<>
			<Layout className='!bg-secondaryBackground'>
				<SearchHeader
					segment={segment}
					keyword={keyword}
					tabStatus={tabStatus}
					collection={collection}
					setKeyword={setKeyword}
					pageNo={pageInfo?.pageNo}
					pageSize={pageInfo?.pageSize}
					selectedRowKeys={selectedRowKeys}
					setFilteredOrdersCount={setFilteredOrdersCount}
					setSearchData={setSearchData}
					collectionData={collectionData}
					filterProperty={filterProperty}
					advancedSearch={advancedSearch}
					selectedOrders={selectedOrders}
					setFilterProperty={setFilterProperty}
					showMissingKanta={showMissingKanta}
					setShowMissingKanta={setShowMissingKanta}
				/>
				{loading ? (
					<Loader />
				) : (
					<Layout className='!bg-white rounded-lg overflow-y-hidden shadow-[0_1px_8px_rgba(0,0,0,0.16)]'>
						<Content>
							<TableHeader
								segment={segment}
								filteredCount={filteredOrdersCount}
								totalCount={collectionData?.length}
								tabStatus={tabStatus}
								selectedRowKeys={selectedRowKeys}
							/>
							<ErrorBoundary
								fallbackRender={({ error, resetErrorBoundary }) => (
									<FallBackUI component={'DynamicTable DynamicTable'} error={error} resetErrorBoundary={resetErrorBoundary} />
								)}
								resetKeys={[searchData, tabStatus]}
							>
								<DynamicTable
									segment={segment}
									tabStatus={tabStatus}
									rowClassName={tabStatus === 'issuance' ? '!h-32' : '!h-28'}
									searchData={searchData}
									collection={collection}
									selectedOrders={selectedOrders}
									columns={biltyManagementColumns}
									selectedRowKeys={selectedRowKeys}
									tableHeight={'calc(100vh - 410px)'}
									setSelectedRowKeys={setSelectedRowKeys}
									onRow={(record) => {
										return {
											onClick: (e) => {
												if (tabStatus === 'inProgress' || tabStatus === 'completed') {
													navigator(`/biltyManagement/${orderType}/${tabStatus}/job/${record?._id}/sideDrawer`)
												} else {
													e.stopPropagation()
												}
											},
										}
									}}
									columnProps={{ segment, tabStatus, collectionData, selectedOrders, biltyOpUsers, user, navigator }}
								/>
							</ErrorBoundary>
						</Content>
						{selectedOrders?.length === 0 && (
							<Footer className='flex justify-center items-center !h-16 !bg-white !py-2 !border-t-[0.5px] !border-[rgba(0, 0, 0, 0.12)]'>
								<Pagination setPageInfo={setPageInfo} setBackendPageInfo={setBackendPageInfo} totalCount={filteredOrdersCount} pageInfo={pageInfo} />
							</Footer>
						)}
					</Layout>
				)}
			</Layout>
			<LevelContext.Provider
				value={{
					loading,
					modalType,
					props: {
						segment,
						trackers,
						setSelectedRowKeys,
						...(tabStatus !== 'issuance' && segment === 'LongHaul' ? { isLot: modalData?.[0]?.subOrders?.[0]?.preferences?.lotOrder } : {}),
						data: {
							job: modalData?.[0],
							selectedBiltyNumber,
							record:
								tabStatus === 'issuance'
									? documentOrdersMapFunction(modalData)
									: tabStatus === 'inProgress' && modalData?.[0]?.subOrders?.[0]?.preferences?.lotOrder && modalType === 'createBilty'
									? documentOrdersMapFunction(modalData?.[0]?.subOrders)
									: modalData?.[0],
						},
					},
				}}
			>
				<Outlet />
			</LevelContext.Provider>
		</>
	)
}

export default BiltyManagementTable
